import { useCallback } from 'react'
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import TSTable from 'components/Tanstack'
import { useMerchantBankAccountsStore } from 'store/merchantBankAccounts'
import { useTableUtils } from 'hooks/useTableUtils'
import {
  MerchantBankAccountsDataTypes,
  MerchantBankAccountsModalActionsTypes,
} from 'types/merchantBankAccounts'
import { hasingOut } from 'services/helpers'
import { useCanUI } from 'hooks/useCanUI'

export const useMerchantBankAccountsTable = () => {
  const { isMerchantBankAccountUpdateBank } = useCanUI()
  const bankAccounts = useMerchantBankAccountsStore(
    (state) => state.merchantBankAccounts
  )

  const {
    selectedModal,
    selectedModalSet,
    selectedRowData,
    selectedRowDataSet,
  } = useTableUtils<
    MerchantBankAccountsDataTypes,
    MerchantBankAccountsModalActionsTypes
  >()

  const handleActionModalClose = useCallback(() => {
    selectedRowDataSet(null)
    selectedModalSet(null)
  }, [selectedModalSet, selectedRowDataSet])

  const columnHelper = createColumnHelper<MerchantBankAccountsDataTypes>()
  const columns = [
    columnHelper.accessor('name', {
      header: () => 'Account Name',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('bankAccountNumber', {
      header: () => 'Account Number',
      cell: (info) => hasingOut(info.getValue()),
    }),
    columnHelper.accessor('expiryDate', {
      header: () => 'Expiry Date',
      cell: (info) => info.getValue()?.slice(0, 10) || '---',
    }),
    // columnHelper.accessor('safetyCode', {
    //   header: () => 'Security Code',
    //   cell: (info) => info.getValue() || '---',
    // }),
    columnHelper.accessor('payerPartyCode', {
      header: () => 'Payer Party Code',
      cell: (info) => info.getValue() || '---',
    }),
    columnHelper.accessor('payerPartyParticulars', {
      header: () => 'Payer Party Particulars',
      cell: (info) => info.getValue() || '---',
    }),
    columnHelper.accessor('payeePartyCode', {
      header: () => 'Payee Party Code',
      cell: (info) => info.getValue() || '---',
    }),
    columnHelper.accessor('payeePartyParticulars', {
      header: () => 'Payee Party Particulars',
      cell: (info) => info.getValue() || '---',
    }),
    columnHelper.accessor('payeePartyRef', {
      header: () => 'Payee Party Ref',
      cell: (info) => info.getValue() || '---',
    }),
    columnHelper.accessor('isDefaultBankAccount', {
      header: () => 'Default',
      cell: (info) => (info.getValue() === 1 ? 'Yes' : 'No'),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => '',
      cell: ({ row }) => (
        <div className="flex gap-2 px-4">
          {isMerchantBankAccountUpdateBank && (
            <TSTable.TableCellButton
              onClick={() => {
                selectedRowDataSet(row.original)
                selectedModalSet('merchant_bank_accounts_update')
              }}
              label="Bank Account Update"
              icon={faPencilAlt}
            />
          )}
        </div>
      ),
    }),
  ] as ColumnDef<MerchantBankAccountsDataTypes>[]

  return {
    merchantBankAccountsSelected: selectedRowData,
    merchantBankAccountsActionModal: selectedModal,
    merchantBankAccountsTableData: bankAccounts,
    merchantBankAccountsTableColumns: columns,
    handleMerchantActionModalClose: handleActionModalClose,
  }
}
